import type { UsageModelTotals, UsageSummary } from "../types";
import { formatCost, stepLabel } from "../utils/format";
import { TABLE, TBODY, TD, TD_NUMBER, TH, TH_NUMBER, THEAD } from "./tableClasses";

const STEP_CONTENTS: { step: string; text: string }[] = [
  {
    step: "chat",
    text: "The model calls of the chat agent in the turn: its prompts, tool results that it reads again, and its answers.",
  },
  {
    step: "map",
    text: "The reader calls of a map: one call for each paper or document, from the CLI or from a Datasets run.",
  },
  { step: "verify", text: "The claim verifier calls that check each value against its source." },
  { step: "reduce", text: "The calls that combine the results of a map into one answer." },
  {
    step: "jev",
    text: "TypeSafe extraction calls. The cost is the cost that TypeSafe reports for its calls.",
  },
  { step: "ask_image", text: "The Gemini calls that read a figure or a page image." },
  {
    step: "read",
    text: "Read-once calls. Read-once has no price for each call, so only the calls and the model time are counted.",
  },
  { step: "search", text: "The Gemini calls that filter search results for relevance." },
  { step: "other", text: "Model calls of the inference endpoint that are not in a step above." },
];

const NOT_INCLUDED = [
  "Search servers: OpenSearch, Qdrant, and slab-grep",
  "Cloud Run, the databases, and file storage",
  "PDF parsing by Datalab, and the embeddings of search indexes",
  "The servers of TypeSafe and read-once (only the costs that TypeSafe reports are counted)",
  "Google Search grounding fees, and the fast mode or US-only routing surcharges of providers",
  "Batch and Flex discounts: all prices are the standard prices",
];

const formatPrice = (usd: number) =>
  `$${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;

const longPromptText = (price: NonNullable<UsageModelTotals["price"]>) => {
  if (!price.long_prompt_tokens) return "—";
  const input = price.long_prompt_input_multiplier ?? 1;
  const output = price.long_prompt_output_multiplier ?? input;
  const multipliers = input === output ? `${input}× each price` : `${input}× input, ${output}× output`;
  return `Over ${price.long_prompt_tokens.toLocaleString("en-US")} tokens: ${multipliers}`;
};

const noPriceText = (row: UsageModelTotals) => {
  if (!row.model) return "The entry has no model.";
  if (row.model === "read-once") return "Read-once has no price for each call.";
  if (row.provider === "typesafe" || row.model.startsWith("typesafe/")) {
    return "Uses the cost that TypeSafe reports. Calls without a reported cost have no price.";
  }
  return "Not in the price table. Its calls have a cost only when the provider reports one.";
};

export const CostLegend = ({ summary }: { summary: UsageSummary }) => {
  const models = summary.models ?? [];
  return (
    <div className="space-y-6 text-sm text-zinc-700">
      <div className="space-y-2">
        <h3 className="font-semibold text-zinc-900">How each cost is estimated</h3>
        <p className="max-w-3xl">
          Each entry is priced when it is recorded, from the tokens of the call. A price change
          does not change the cost of older runs.
        </p>
        <ul className="max-w-3xl list-disc space-y-1 pl-5">
          <li>
            When the provider reports a cost for the call (OpenRouter, TypeSafe), the page uses that
            cost.
          </li>
          <li>
            When the chat agent runs in Claude Code on Claude Opus 4.x or 5, or on Claude Sonnet
            4.x, the page uses the cost estimate of Claude Code.
          </li>
          <li>
            All other calls, including the chat agent on the Claude 5.5 models, Claude Sonnet 5, and
            GPT-6 Astra, use the price table below.
          </li>
        </ul>
        <p className="max-w-3xl">
          Cost = input tokens × input price + cache reads × cache read price + cache writes × cache
          write price + output tokens × output price. In this formula, input tokens are only the
          tokens that are not read from or written to the cache. Output tokens include thinking
          tokens.
        </p>
      </div>

      <div className="space-y-2">
        <h3 className="font-semibold text-zinc-900">What each step includes</h3>
        <dl className="grid max-w-4xl gap-x-6 gap-y-2 sm:grid-cols-[max-content_1fr]">
          {STEP_CONTENTS.map((item) => (
            <div key={item.step} className="contents">
              <dt className="font-medium text-zinc-900">{stepLabel(item.step)}</dt>
              <dd>{item.text}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="space-y-2">
        <h3 className="font-semibold text-zinc-900">Not in the costs</h3>
        <ul className="max-w-3xl list-disc space-y-1 pl-5">
          {NOT_INCLUDED.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>

      <div className="space-y-2">
        <h3 className="font-semibold text-zinc-900">Prices of the models in this period</h3>
        <p className="max-w-3xl">
          USD for each 1 million tokens. List prices, checked on {summary.prices_checked_on},
          against the pricing pages of Anthropic, Google, and OpenRouter.
        </p>
        {models.length === 0 ? (
          <p className="text-zinc-500">No model calls in this period.</p>
        ) : (
          <div
            role="region"
            aria-label="Prices of the models in this period"
            tabIndex={0}
            className="overflow-x-auto rounded-[3px] border border-zinc-200"
          >
            <table className={TABLE}>
              <thead className={THEAD}>
                <tr>
                  <th scope="col" className={TH}>
                    Model
                  </th>
                  <th scope="col" className={TH}>
                    Steps
                  </th>
                  <th scope="col" className={TH_NUMBER}>
                    Input
                  </th>
                  <th scope="col" className={TH_NUMBER}>
                    Cache read
                  </th>
                  <th scope="col" className={TH_NUMBER}>
                    Cache write
                  </th>
                  <th scope="col" className={TH_NUMBER}>
                    Output
                  </th>
                  <th scope="col" className={TH}>
                    Long prompts
                  </th>
                  <th scope="col" className={TH_NUMBER}>
                    Cost in period
                  </th>
                </tr>
              </thead>
              <tbody className={TBODY}>
                {models.map((row) => (
                  <tr key={`${row.provider ?? ""}:${row.model ?? ""}`}>
                    <th scope="row" className={`${TD} text-left font-medium text-zinc-900`}>
                      {row.model ?? "No model"}
                      {row.price?.source === "openrouter" && (
                        <span className="block text-xs font-normal text-zinc-500">
                          Price from OpenRouter
                        </span>
                      )}
                    </th>
                    <td className={TD}>{row.steps.map(stepLabel).join(", ")}</td>
                    {row.price ? (
                      <>
                        <td className={TD_NUMBER}>{formatPrice(row.price.input)}</td>
                        <td className={TD_NUMBER}>{formatPrice(row.price.cache_read)}</td>
                        <td className={TD_NUMBER}>{formatPrice(row.price.cache_write)}</td>
                        <td className={TD_NUMBER}>{formatPrice(row.price.output)}</td>
                        <td className={TD}>{longPromptText(row.price)}</td>
                      </>
                    ) : (
                      <td colSpan={5} className={`${TD} text-zinc-500`}>
                        {noPriceText(row)}
                      </td>
                    )}
                    <td className={TD_NUMBER}>
                      {formatCost(row.cost_usd)}
                      {row.unpriced_events > 0 && (
                        <span className="block text-xs text-zinc-500">
                          {row.unpriced_events.toLocaleString()} without a price
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
